import * as React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { documentEditingService, type DocumentVersionInfo } from "../../services/documentEditingService";
import { canPreviewInline, isEditableImage, isPdfLike, isTextLike } from "./documentKind";
import { documentService } from "../../services/documentService";
import { bestaetigen } from "../redesign/bestaetigen";
import { Z_LAYERS } from "../windowManager/zLayers";
import { PdfIcons } from "./pdf-editor/pdfIcons";
import { baueFassungsListe, type AktuellesDokument, type FassungsEintrag } from "./fassungsListe";
import "./pdf-editor/pdfEditor.css";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";

/**
 * Die Fassungen eines Dokuments: ansehen, herunterladen, wiederherstellen.
 *
 * Eigenes Popup im kvp-rd-Stil, kein Fluent (User 07.10.2026) — dieselbe Hülle
 * wie „Fassung speichern“ (`pdf-editor/SpeichernPopup`), portaliert nach
 * `document.body` auf `Z_LAYERS.ueberFenster`, weil es meist aus einem Fenster
 * des Fenster-Managers heraus geöffnet wird.
 *
 * Gezeigt wird je STAND, was dazu gehört (`fassungsListe.ts`): der Name, wie
 * die Datei davor hieß, der Kommentar und darunter, was geändert wurde.
 *
 * Die Liste wird erst beim Öffnen geladen — die allermeisten der 65.000 PDFs
 * haben gar keine Fassungen, ein Abruf beim bloßen Betrachten wäre für nahezu
 * jeden Aufruf umsonst.
 */

const formatSize = (bytes: number | null): string => {
    if (!bytes || bytes < 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (iso: string | null): string => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

/** Lässt sich diese Fassung im KMVS-PDF öffnen (und dort weiterbearbeiten)? */
const imKmvsPdf = (fileName: string): boolean => isPdfLike(fileName) || isEditableImage(fileName);

function herunterladen(blob: Blob, name: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export const DocumentVersionsDialog: React.FC<{
    docId: number;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Nach dem Wiederherstellen — der Aufrufer lädt das Dokument neu. */
    onRestored: () => void;
    canRestore?: boolean;
    /**
     * „Ansehen“ einer PDF- oder Bild-Fassung im KMVS-PDF (User 07.10.2026) —
     * dort lässt sie sich auch weiterbearbeiten. Ohne diese Angabe (Office-
     * Ansicht) bleibt es bei der Vorschau im Popup.
     */
    onAnsehen?: (version: DocumentVersionInfo) => void;
}> = ({ docId, open, onOpenChange, onRestored, canRestore = true, onAnsehen }) => {
    const [versions, setVersions] = React.useState<DocumentVersionInfo[] | null>(null);
    const [doc, setDoc] = React.useState<AktuellesDokument | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [preview, setPreview] = React.useState<{ versionNo: number; fileName: string; url?: string; text?: string } | null>(null);
    // Versionsnummer, 0 = aktueller Stand, `null` = nichts läuft.
    const [busy, setBusy] = React.useState<number | null>(null);
    const previewGeneration = React.useRef(0);

    React.useEffect(() => {
        if (!open) return;
        let alive = true;
        setVersions(null);
        setDoc(null);
        setError(null);
        documentEditingService
            .listVersions(docId)
            .then((rows) => alive && setVersions(rows))
            .catch(() => alive && setError("Die Fassungen konnten nicht geladen werden."));
        // Name und Größe des aktuellen Standes — fehlt das, steht die Liste trotzdem.
        documentService
            .getOne(docId)
            .then((d) => alive && setDoc({ fileName: d.fileName, fileSize: d.fileSize ?? null, uploadedAt: d.uploadedAt ?? null }))
            .catch(() => undefined);
        return () => {
            alive = false;
        };
    }, [docId, open]);

    React.useEffect(
        () => () => {
            if (preview?.url) URL.revokeObjectURL(preview.url);
        },
        [preview],
    );
    React.useEffect(() => {
        previewGeneration.current += 1;
        setPreview(null);
        setBusy(null);
        return () => {
            previewGeneration.current += 1;
        };
    }, [docId, open]);

    // Esc schließt — außer es liegt gerade die Rückfrage zum Wiederherstellen darüber.
    const offenRef = React.useRef({ onOpenChange, rueckfrage: false });
    offenRef.current.onOpenChange = onOpenChange;
    React.useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape" && !offenRef.current.rueckfrage) offenRef.current.onOpenChange(false);
        };
        alleFenster.addEventListener("keydown", onKey);
        return () => alleFenster.removeEventListener("keydown", onKey);
    }, [open]);

    const vorschauSchliessen = () => {
        previewGeneration.current += 1;
        setPreview(null);
        setBusy(null);
    };

    const showPreview = async (v: DocumentVersionInfo) => {
        const generation = ++previewGeneration.current;
        setBusy(v.versionNo);
        setError(null);
        try {
            const blob = await documentEditingService.fetchVersionBlob(docId, v.versionNo);
            if (previewGeneration.current !== generation) return;
            if (isTextLike(v.fileName)) {
                const text = await blob.text();
                if (previewGeneration.current === generation) setPreview({ versionNo: v.versionNo, fileName: v.fileName, text });
            } else {
                setPreview({
                    versionNo: v.versionNo,
                    fileName: v.fileName,
                    url: URL.createObjectURL(isPdfLike(v.fileName) ? new Blob([blob], { type: "application/pdf" }) : blob),
                });
            }
        } catch {
            if (previewGeneration.current === generation) setError("Die Fassung konnte nicht angezeigt werden.");
        } finally {
            if (previewGeneration.current === generation) setBusy(null);
        }
    };

    const ansehen = (v: DocumentVersionInfo) => {
        if (onAnsehen && imKmvsPdf(v.fileName)) {
            onOpenChange(false);
            onAnsehen(v);
            return;
        }
        void showPreview(v);
    };

    const downloadCurrent = async () => {
        setBusy(0);
        setError(null);
        try {
            const aktuell = await documentService.getOne(docId);
            herunterladen(await documentService.fetchBlob(docId), aktuell.fileName);
        } catch {
            setError("Der aktuelle Endstand konnte nicht heruntergeladen werden.");
        } finally {
            setBusy(null);
        }
    };

    const download = React.useCallback(
        async (v: DocumentVersionInfo) => {
            setBusy(v.versionNo);
            setError(null);
            try {
                herunterladen(await documentEditingService.fetchVersionBlob(docId, v.versionNo), `v${v.versionNo}-${v.fileName}`);
            } catch {
                setError("Die Fassung konnte nicht heruntergeladen werden.");
            } finally {
                setBusy(null);
            }
        },
        [docId],
    );

    const restore = React.useCallback(
        async (v: DocumentVersionInfo) => {
            offenRef.current.rueckfrage = true;
            const ok = await bestaetigen({
                titel: `Fassung ${v.versionNo} wiederherstellen?`,
                text: "Der aktuelle Stand geht nicht verloren — er wird zuvor als neue Fassung gesichert.",
                ja: "Wiederherstellen",
            }).finally(() => {
                offenRef.current.rueckfrage = false;
            });
            if (!ok) return;
            setBusy(v.versionNo);
            try {
                await documentEditingService.restoreVersion(docId, v.versionNo);
                onOpenChange(false);
                onRestored();
            } catch (err) {
                setError(err instanceof Error ? err.message : "Das Wiederherstellen ist fehlgeschlagen.");
            } finally {
                setBusy(null);
            }
        },
        [docId, onOpenChange, onRestored],
    );

    if (!open) return null;

    const eintraege = versions ? baueFassungsListe(versions, doc) : [];
    const nurAktuell = versions?.length === 0;

    const zeile = (e: FassungsEintrag) => {
        const v = e.version;
        const meta = [
            e.office ? `Lokal gesichert: ${formatDate(e.wann)}` : formatDate(e.wann),
            e.wer,
            formatSize(e.fileSize),
        ].filter(Boolean);
        const kannAnsehen = !!v && ((!!onAnsehen && imKmvsPdf(v.fileName)) || canPreviewInline(v.fileName));
        return (
            <li key={e.key} className={`pdfx-fs-zeile${v ? "" : " aktuell"}`}>
                <div className="pdfx-fs-kopf">
                    <span className="pdfx-fs-nr">{e.titel}</span>
                    {e.original && v && <span className="pdfx-fs-marke">Original</span>}
                    {e.office && <span className="pdfx-fs-marke">Office-Sicherung</span>}
                </div>
                <div className="pdfx-fs-name" title={e.fileName}>
                    {e.fileName}
                </div>
                {e.vorherName && (
                    <div className="pdfx-fs-vorher" title={e.vorherName}>
                        vorher: {e.vorherName}
                    </div>
                )}
                {e.kommentar && <div className="pdfx-fs-kommentar">„{e.kommentar}“</div>}
                {e.aenderung && <div className="pdfx-fs-aenderung">Geändert: {e.aenderung}</div>}
                {e.office && (
                    <div className="pdfx-fs-aenderung">
                        Systemaktion: Office-Sicherung · Microsoft-Stand:{" "}
                        {e.office.microsoftStand ? formatDate(e.office.microsoftStand) : "Zeit nicht verfügbar"}
                        {e.office.original ? " · Original / lokal gespeicherter Stand" : ""}
                    </div>
                )}
                {meta.length > 0 && <div className="pdfx-fs-meta">{meta.join(" · ")}</div>}
                <div className="pdfx-fs-aktionen">
                    {v && kannAnsehen && (
                        <button
                            type="button"
                            className="pdfx-btn klein"
                            aria-label={`Fassung ${v.versionNo} ansehen`}
                            disabled={busy != null}
                            onClick={() => ansehen(v)}
                        >
                            Ansehen
                        </button>
                    )}
                    <button
                        type="button"
                        className="pdfx-btn klein"
                        aria-label={v ? `Fassung ${v.versionNo} herunterladen` : "Aktuelle Fassung herunterladen"}
                        disabled={busy != null}
                        onClick={() => void (v ? download(v) : downloadCurrent())}
                    >
                        {PdfIcons.herunterladen}
                        Herunterladen
                    </button>
                    {v && canRestore && (
                        <button
                            type="button"
                            className="pdfx-btn klein"
                            aria-label={`Fassung ${v.versionNo} wiederherstellen`}
                            disabled={busy != null}
                            onClick={() => void restore(v)}
                        >
                            {PdfIcons.fassungen}
                            Wiederherstellen
                        </button>
                    )}
                </div>
            </li>
        );
    };

    return createPortal(
        <div
            className="pdfx pdfx-scrim"
            style={{ zIndex: Z_LAYERS.ueberFenster }}
            onMouseDown={(e) => e.target === e.currentTarget && onOpenChange(false)}
        >
            <div className="pdfx-popup pdfx-fs" role="dialog" aria-modal="true" aria-labelledby="pdfx-fassungen-titel">
                <div className="pdfx-popup-kopf">
                    <div className="pdfx-popup-kicker">Historie</div>
                    <h2 id="pdfx-fassungen-titel">Fassungen</h2>
                </div>
                <div className="pdfx-popup-body pdfx-fs-body">
                    {error && (
                        <p className="pdfx-fs-fehler" role="alert">
                            {error}
                        </p>
                    )}
                    {preview && (
                        <div className="pdfx-fs-vorschau">
                            <div className="pdfx-fs-vorschau-kopf">
                                <strong>Fassung {preview.versionNo}</strong>
                                <button type="button" className="pdfx-btn klein" onClick={vorschauSchliessen}>
                                    Vorschau schließen
                                </button>
                            </div>
                            {preview.text != null ? (
                                <pre>{preview.text}</pre>
                            ) : isPdfLike(preview.fileName) ? (
                                <iframe title={`Fassung ${preview.versionNo}`} src={preview.url} />
                            ) : (
                                <img alt={`Fassung ${preview.versionNo}`} src={preview.url} />
                            )}
                        </div>
                    )}
                    {!versions && !error && (
                        <p className="pdfx-popup-text" role="status">
                            Wird geladen…
                        </p>
                    )}
                    {versions && <ol className="pdfx-fs-liste">{eintraege.map(zeile)}</ol>}
                    {nurAktuell && (
                        <p className="pdfx-note" role="note">
                            Dieses Dokument wurde noch nicht bearbeitet — es gibt nur den aktuellen Stand.
                        </p>
                    )}
                    {versions && versions.length > 0 && (
                        <p className="pdfx-note" role="note">
                            Fassung 1 ist der Stand vor der ersten Bearbeitung.{" "}
                            {onAnsehen
                                ? "„Ansehen“ öffnet eine Fassung im KMVS-PDF — dort lässt sie sich auch weiterbearbeiten. "
                                : ""}
                            Beim Wiederherstellen wird der aktuelle Stand zuvor gesichert — es geht nichts verloren.
                        </p>
                    )}
                </div>
                <div className="pdfx-popup-foot">
                    <button type="button" className="pdfx-btn" onClick={() => onOpenChange(false)}>
                        Schließen
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
};
